import { useEffect, useRef, useState } from 'react';
import type { DocumentListing } from '../lib/types';
import { ApiError, api } from '../lib/api';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatDate(iso: string): string {
  if (iso === '') return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
}

/**
 * Files stored against one resource — certificates, manuals, scan
 * reports.
 *
 * Downloads go through the API for a fresh signed URL per click rather
 * than embedding one in the list. A URL minted when the panel opened
 * would expire while someone read the page, and every link on screen
 * would be a live, shareable credential for as long as it lasted.
 */
export function DocumentList({
  equipmentId,
  canUpload,
}: {
  equipmentId: number;
  /** Schedulers and admins may add files; viewers only read. */
  canUpload: boolean;
}) {
  const [listing, setListing] = useState<DocumentListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setListing(null);
    setError(null);
    setNotConfigured(false);
    api
      .listDocuments(equipmentId)
      .then((rows) => {
        if (!cancelled) setListing(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A missing bucket is a deployment state, not a fault worth a
        // red banner on every resource anyone opens.
        if (err instanceof ApiError && err.code === 'DOCUMENTS_NOT_CONFIGURED') {
          setNotConfigured(true);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [equipmentId, reloadToken]);

  async function download(key: string) {
    setBusyKey(key);
    setError(null);
    try {
      const { url } = await api.documentUrl(equipmentId, key);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(
        'Could not open that file: ' +
          (err instanceof Error ? err.message : String(err)),
      );
    }
    setBusyKey(null);
  }

  async function upload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const grant = await api.requestUpload(
        equipmentId,
        file.name,
        file.type || 'application/octet-stream',
      );

      if (file.size > grant.maxBytes) {
        // S3 would reject it anyway through the policy; saying so here
        // saves sending the bytes first.
        throw new Error(
          `That file is ${(file.size / 1024 / 1024).toFixed(0)} MB. The limit is ` +
            `${Math.round(grant.maxBytes / 1024 / 1024)} MB.`,
        );
      }

      // Policy fields must precede the file part — S3 ignores anything
      // that arrives after it.
      const form = new FormData();
      for (const [k, v] of Object.entries(grant.fields)) form.append(k, v);
      form.append('file', file);

      const res = await fetch(grant.url, { method: 'POST', body: form });
      if (!res.ok) {
        // S3 answers with an XML <Error><Message>, not JSON.
        const text = await res.text().catch(() => '');
        const detail = /<Message>([^<]+)<\/Message>/.exec(text)?.[1];
        throw new Error(detail ?? `S3 rejected the upload (${res.status})`);
      }

      setReloadToken((n) => n + 1);
    } catch (err) {
      setError(
        'Upload failed: ' + (err instanceof Error ? err.message : String(err)),
      );
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = '';
  }

  // Nothing configured: say nothing. The section would just be noise.
  if (notConfigured) return null;

  return (
    <>
      <div
        className="section-title"
        style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span>Documents</span>
        <span style={{ flex: 1 }} />
        {canUpload && (
          <>
            <input
              ref={fileInput}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
              }}
            />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              style={{
                fontSize: 11,
                padding: '3px 9px',
                borderRadius: 6,
                background: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
                border: '0.5px solid var(--border-md)',
              }}
            >
              {uploading ? 'Uploading…' : '+ Add file'}
            </button>
          </>
        )}
      </div>

      {error !== null && (
        <div style={{ fontSize: 12, color: 'var(--red-fg)', marginBottom: 6 }}>
          {error}
        </div>
      )}

      {listing === null && error === null ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          Loading files…
        </div>
      ) : listing !== null && listing.documents.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          No files yet. Add them under{' '}
          <code style={{ fontSize: 11 }}>{listing.location}</code>
        </div>
      ) : (
        listing !== null && (
          <>
            {listing.documents.map((doc) => (
              <div
                key={doc.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 0',
                  borderTop: '0.5px solid var(--border)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {formatSize(doc.sizeBytes)}
                    {doc.lastModified ? ` · ${formatDate(doc.lastModified)}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => void download(doc.key)}
                  disabled={busyKey === doc.key}
                  style={{
                    fontSize: 11,
                    padding: '3px 9px',
                    borderRadius: 6,
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    border: '0.5px solid var(--border-md)',
                  }}
                >
                  {busyKey === doc.key ? 'Opening…' : 'Download'}
                </button>
              </div>
            ))}
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 6,
              }}
            >
              <code>{listing.location}</code>
            </div>
          </>
        )
      )}
    </>
  );
}
